import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import QuoteForm from '../../../components/feature/QuoteForm';

export default function CommercialAsphalt() {
  const services = [
    {
      title: 'Parking Lot Paving',
      description: 'New parking lot construction and complete replacement for commercial properties.',
      icon: 'ri-parking-box-line',
    },
    {
      title: 'Asphalt Sealing',
      description: 'Commercial sealcoating services that protect pavement and enhance appearance.',
      icon: 'ri-paint-brush-line',
    },
    {
      title: 'Concrete Repair',
      description: 'Commercial concrete repair for sidewalks, loading docks, and building entrances.',
      icon: 'ri-hammer-line',
    },
    {
      title: 'Line Striping',
      description: 'Professional parking lot striping, ADA compliance, and traffic markings.',
      icon: 'ri-road-map-line',
    },
    {
      title: 'Pavement Maintenance',
      description: 'Scheduled maintenance programs that maximize pavement life and minimize costs.',
      icon: 'ri-calendar-check-line',
    },
    {
      title: 'Emergency Repairs',
      description: 'Fast response for urgent repairs that affect safety or business operations.',
      icon: 'ri-alert-line',
    },
  ];

  const faqs = [
    {
      question: 'How much does commercial parking lot paving cost in Lincoln NE?',
      answer: 'Commercial asphalt paving costs $3-$7 per square foot for new construction, $2-$5 per square foot for overlay. A 10,000 sq ft parking lot costs $30,000-$70,000 for complete replacement, $20,000-$50,000 for overlay. Prices vary based on site conditions, thickness, and project complexity. We provide detailed estimates after site inspection.',
    },
    {
      question: 'How long does commercial paving take?',
      answer: 'Small parking lots (5,000-10,000 sq ft) take 3-5 days. Medium lots (10,000-30,000 sq ft) take 1-2 weeks. Large projects take 2-4 weeks. Timeline depends on size, weather, and site conditions. We work efficiently to minimize business disruption and can schedule work during off-hours or weekends.',
    },
    {
      question: 'Do you offer maintenance contracts?',
      answer: 'Yes! Our commercial maintenance contracts include scheduled inspections, crack filling, sealcoating every 2-3 years, line striping, and priority service for repairs. Contracts budget maintenance costs, ensure timely service, and maximize pavement life. We customize programs based on property size, traffic, and budget.',
    },
    {
      question: 'What is commercial asphalt sealing and how often is it needed?',
      answer: 'Commercial sealcoating protects asphalt from water, UV rays, chemicals, and traffic wear. Apply every 2-3 years for optimal protection. High-traffic areas may need annual sealing. Sealcoating costs $0.15-$0.30 per square foot. A 10,000 sq ft lot costs $1,500-$3,000. Regular sealing doubles pavement life.',
    },
    {
      question: 'Can you work around our business hours?',
      answer: 'Absolutely! We schedule commercial projects to minimize disruption. Work during evenings, weekends, or off-season when traffic is light. We phase large projects to keep portions of parking available. Our crews work efficiently and communicate clearly about access and timing.',
    },
    {
      question: 'Do you handle ADA compliance and accessibility?',
      answer: 'Yes! We ensure parking lots meet ADA requirements: proper handicap space dimensions, van-accessible spaces, access aisles, signage, and accessible routes to building entrances. We assess existing lots for compliance issues and make necessary corrections. ADA compliance protects businesses from liability and serves all customers.',
    },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-primary-500 text-sm mb-4 tracking-wider">Services / Commercial</p>
            <h1 className="text-5xl md:text-6xl font-black mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Commercial Asphalt Paving
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Professional parking lot paving, asphalt sealing, and concrete repair for Lincoln businesses
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div>
              <img
                src="https://static.readdy.ai/image/cd984ab7ba08a0f8b6f75adc7751930e/9c9270c16bcc777eaef661b4a3d967f9.jpeg"
                alt="Lincoln NE commercial asphalt paving and parking lot services"
                className="w-full h-96 object-cover rounded-2xl shadow-xl"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Professional Commercial Paving
              </h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Your parking lot is the first impression customers have of your business. Cracked, faded, or poorly maintained pavement sends the wrong message. Our commercial paving services create attractive, durable parking areas that enhance your property and serve customers safely.
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                We specialize in complete parking lot construction, asphalt overlay, sealcoating, line striping, and concrete repairs for Lincoln businesses. From small retail lots to large industrial facilities, we deliver quality workmanship that meets commercial standards and budgets.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Serving Lincoln's commercial property owners for over 20 years, we understand the importance of minimizing business disruption. We schedule work during off-hours, work efficiently, and communicate clearly throughout the project. Our maintenance contracts provide ongoing care that maximizes pavement life.
              </p>
              <a href="tel:3084705637" className="inline-block bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap">
                CALL (308) 470-5637
              </a>
            </div>
          </div>

          {/* Services Grid */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Commercial Services
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-2xl border-2 border-gray-200 hover:border-primary-600 transition-all">
                  <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mb-6">
                    <i className={`${service.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {service.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Detailed Content */}
          <div className="prose prose-lg max-w-none mb-16">
            <h2 className="text-4xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Commercial Asphalt Paving in Lincoln NE
            </h2>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Commercial pavement faces challenges residential driveways never encounter: heavy traffic, large vehicles, constant use, and high visibility. Your parking lot must handle daily traffic while maintaining professional appearance. Poor pavement creates safety hazards, damages vehicles, and reflects poorly on your business.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Professional commercial paving delivers results that last. We use heavier asphalt thickness (3-4 inches versus 2-3 for residential), stronger base materials, proper drainage design, and quality construction techniques. Our commercial projects meet industry standards and local codes while staying on schedule and budget.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Parking Lot Paving and Construction
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              New parking lot construction requires careful planning and execution. We handle every phase: site evaluation, drainage design, base preparation, asphalt paving, line striping, and final inspection. Proper design maximizes parking capacity while ensuring safe traffic flow and ADA compliance.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Our construction process starts with site preparation: clearing, grading, and establishing proper drainage. We install 6-8 inches of compacted aggregate base for stability. Asphalt is paved 3-4 inches thick for commercial traffic. We use quality hot mix asphalt that meets specifications for strength and durability.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Commercial paving costs $3-$7 per square foot for new construction. A typical 10,000 sq ft parking lot costs $30,000-$70,000 complete. Prices vary based on site conditions, base requirements, thickness, and project complexity. We provide detailed estimates that break down all costs.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Parking Lot Overlay and Resurfacing
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              When existing pavement is structurally sound but surface-worn, overlay (resurfacing) provides cost-effective renewal. We mill the existing surface, repair any base problems, and pave with 1.5-2 inches of new asphalt. The result looks and performs like new pavement at 50-70% the cost of replacement.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Overlay works best when damage is surface-level (oxidation, minor cracking, wear) rather than structural (alligator cracking, potholes, base failure). We assess your parking lot and recommend overlay or replacement based on condition. Overlay costs $2-$5 per square foot versus $3-$7 for complete replacement.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Commercial Asphalt Sealing
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Sealcoating is essential maintenance for commercial parking lots. This protective coating shields asphalt from water, UV rays, chemicals (oil, gas), and traffic wear. Regular sealing doubles pavement life from 15 to 30+ years while maintaining attractive appearance that reflects well on your business.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We use commercial-grade coal tar or asphalt emulsion sealers formulated for high-traffic areas. Our process includes power cleaning, crack filling, two coats of sealer, and proper curing time. We work during off-hours to minimize business disruption and can phase large lots to maintain partial access.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Commercial sealcoating costs $0.15-$0.30 per square foot. A 10,000 sq ft parking lot costs $1,500-$3,000. Sealcoat every 2-3 years for optimal protection. High-traffic areas may need annual sealing. This small investment prevents expensive repairs and maintains professional appearance.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Commercial Concrete Repair
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Commercial properties have extensive concrete: sidewalks, building entrances, loading docks, dumpster pads, and curbs. Damaged concrete creates trip hazards and liability. We provide complete concrete repair services: crack repair, slab replacement, resurfacing, and new construction.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Our concrete services include ADA-compliant ramps and walkways, loading dock repairs that handle heavy equipment, dumpster pad replacement for waste management areas, and decorative concrete for building entrances. We use commercial-grade concrete (4,000+ PSI) that withstands heavy use.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Concrete repair costs vary by scope: crack repair $3-$8 per linear foot, slab replacement $6-$12 per square foot, new construction $8-$15 per square foot. We assess your concrete and recommend cost-effective solutions that address safety concerns and maintain property value.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Parking Lot Striping and Markings
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Professional line striping maximizes parking capacity, improves traffic flow, and ensures ADA compliance. Faded or missing lines create confusion and reduce usable spaces. We provide complete striping services: parking spaces, fire lanes, handicap spaces, directional arrows, stop bars, and custom markings.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We use high-quality traffic paint or thermoplastic that lasts 1-3 years depending on traffic. Our process includes layout planning to maximize spaces, surface cleaning, precise application with professional equipment, and proper curing. We ensure ADA compliance with proper handicap space dimensions, van-accessible spaces, and signage.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Striping costs $0.15-$0.30 per linear foot for standard parking lots. A typical 50-space lot costs $800-$1,500 for complete striping. Re-striping is needed every 1-3 years as paint wears. We offer maintenance contracts that include scheduled striping to keep your lot looking professional.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              ADA Compliance and Accessibility
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              The Americans with Disabilities Act requires accessible parking and routes to building entrances. Non-compliance creates liability and excludes customers. We ensure parking lots meet ADA requirements: proper number of handicap spaces based on total spaces, correct dimensions (8 feet wide with 5-foot access aisle), van-accessible spaces (11 feet wide), proper signage, and accessible routes with compliant slopes.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We assess existing lots for compliance issues and make necessary corrections. This includes adding handicap spaces, restriping for proper dimensions, installing signage, repairing concrete for accessible routes, and adjusting slopes. ADA compliance protects your business and serves all customers.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Commercial Maintenance Programs
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Our commercial maintenance contracts provide scheduled services that maximize pavement life and minimize costs. Programs include annual inspections, crack filling as needed, sealcoating every 2-3 years, line striping, and priority service for repairs. Preventive maintenance prevents 80% of major repairs.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Maintenance contracts budget costs over multiple years, ensure timely service, and provide priority scheduling. We customize programs based on property size, traffic levels, pavement condition, and budget. Commercial properties benefit from professional management that keeps pavement in top condition.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Minimizing Business Disruption
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We understand commercial projects must minimize disruption to business operations. We schedule work during off-hours, evenings, weekends, or slow seasons when traffic is light. Large projects are phased to keep portions of parking available. We communicate clearly about access, timing, and any temporary inconveniences.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Our experienced crews work efficiently to complete projects quickly without sacrificing quality. We use professional equipment that speeds work while delivering superior results. Clear signage directs customers during construction. We clean up thoroughly and restore full access as quickly as possible.
            </p>
          </div>

          {/* Project Gallery */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Commercial Projects
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <img
                src="https://public.readdy.ai/ai/img_res/4971d4a0be24008889126ee702aed817.jpg"
                alt="Commercial parking lot paving"
                className="w-full h-64 object-cover rounded-2xl shadow-xl"
              />
              <img
                src="https://static.readdy.ai/image/cd984ab7ba08a0f8b6f75adc7751930e/037d079c5d29b6b022961192b874a51e.jpeg"
                alt="Commercial sealcoating"
                className="w-full h-64 object-cover rounded-2xl shadow-xl"
              />
              <img
                src="https://readdy.ai/api/search-image?query=ADA%20compliant%20handicap%20parking%20spaces%20with%20proper%20striping%20and%20signage%20for%20commercial%20property%20in%20Lincoln%20Nebraska%20with%20clean%20simple%20light%20background&width=600&height=400&seq=111&orientation=landscape"
                alt="ADA compliant parking"
                className="w-full h-64 object-cover rounded-2xl shadow-xl"
              />
            </div>
          </div>

          {/* FAQs */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-2xl border-2 border-gray-200">
                  <h3 className="text-2xl font-bold mb-4 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {faq.question}
                  </h3>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Quote Form */}
          <div className="bg-gray-50 p-12 rounded-2xl">
            <h2 className="text-4xl font-bold mb-6 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Request Commercial Quote
            </h2>
            <p className="text-xl text-gray-700 text-center mb-8">
              Professional paving services for Lincoln businesses
            </p>
            <QuoteForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}